import { useEffect, useState } from "react";
import { Loader2, Gauge, UserCheck, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { preconApi } from "../lib/api";
import { EmailsTab, NotesPanel } from "./BidCardTabs";
import { BoardStatusChip } from "./BidStatusChips";
import { openEstimate } from "../lib/navigation";

const FIT_LABEL = {
  gc_rating: "GC rating", scope_match: "Scope match", distance: "Distance",
  days_to_due: "Days to due", hit_rate: "GC hit rate", facility_hit: "Facility hit rate",
};
const d10 = (iso) => (iso ? iso.slice(0, 10) : "—");

const Section = ({ title, action, children }) => (
  <section className="border-b border-slate-200 px-5 py-4">
    <div className="flex items-center justify-between pb-2">
      <h3 className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{title}</h3>
      {action}
    </div>
    {children}
  </section>
);

const Field = ({ label, children }) => (
  <div className="flex items-baseline gap-2 py-0.5 text-xs">
    <span className="w-28 shrink-0 text-[10px] font-medium uppercase tracking-wide text-slate-500">{label}</span>
    <div className="flex-1">{children}</div>
  </div>
);

export const BidJobDrawer = ({ job, onClose, onChanged }) => {
  const [reasons, setReasons] = useState({});
  const [declining, setDeclining] = useState({});
  const [busy, setBusy] = useState(false);
  const [estimate, setEstimate] = useState(null);
  const [people, setPeople] = useState({ estimators: [], account_managers: [] });
  const [addr, setAddr] = useState({ street: job.street || "", city: job.city || "", state: job.state || "" });
  const [dates, setDates] = useState({ start_date: job.start_date || "", completion_date: job.completion_date || "" });

  useEffect(() => {
    preconApi.assignable()
      .then((r) => setPeople({ estimators: r.estimators || [], account_managers: r.account_managers || [] }))
      .catch(() => {});
  }, []);
  useEffect(() => {
    let live = true;
    preconApi.jobEstimate(job.id).then((out) => { if (live) setEstimate(out); }).catch(() => { if (live) setEstimate(null); });
    return () => { live = false; };
  }, [job.id, job.estimate_id, job.status]);

  const run = async (fn, msg) => {
    setBusy(true);
    try { await fn(); if (msg) toast.success(msg); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "That didn't work"); }
    finally { setBusy(false); }
  };

  const patch = (data) => run(() => preconApi.patchJob(job.id, data));
  const proposal = job.assignment_proposal || {};

  return (
    <Sheet open onOpenChange={onClose}>
      <SheetContent className="flex w-full !max-w-3xl flex-col gap-0 overflow-y-auto p-0" data-testid="bid-job-drawer">
        <SheetHeader className="border-b border-slate-200 px-5 py-4">
          <SheetTitle className="text-base" data-testid="bid-job-title">{job.project_name}</SheetTitle>
          <SheetDescription className="sr-only">Bid job details, notes and emails</SheetDescription>
          <div className="flex flex-wrap items-center gap-2 pt-1" data-testid="bid-job-address">
            <Input value={addr.street} placeholder="Street" onBlur={() => patch(addr)}
                   onChange={(e) => setAddr({ ...addr, street: e.target.value })}
                   className="h-6 w-48 text-[11px]" data-testid="bid-job-street" />
            <Input value={addr.city} placeholder="City" onBlur={() => patch(addr)}
                   onChange={(e) => setAddr({ ...addr, city: e.target.value })}
                   className="h-6 w-32 text-[11px]" data-testid="bid-job-city" />
            <Input value={addr.state} placeholder="ST" onBlur={() => patch(addr)}
                   onChange={(e) => setAddr({ ...addr, state: e.target.value })}
                   className="h-6 w-16 text-[11px]" data-testid="bid-job-state" />
          </div>
          <div className="flex flex-wrap items-center gap-2 pt-1 text-[11px] text-slate-500">
            <span className="flex items-center gap-1" data-testid="bid-job-fit"><Gauge className="h-3 w-3" /> fit {job.fit_score ?? "—"}</span>
          </div>
          {job.pending_outcome && (
            <div className="mt-2 flex items-center gap-2 rounded bg-amber-50 px-2 py-1.5 text-[11px] text-amber-900" data-testid="bid-job-pending-outcome">
              A GC email says this is <b>{job.pending_outcome.outcome}</b>. Set it on the Pipeline — Won, Lost and Cancelled only change there.
            </div>
          )}
        </SheetHeader>

        <div className="px-5 py-3"><NotesPanel job={job} onChanged={onChanged} /></div>
        <div className="border-t border-slate-200 px-5 py-3">
          <h3 className="pb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Emails</h3>
          <EmailsTab jobId={job.id} />
        </div>

        <Section title="Details">
          <Field label="Bid status">
            <BoardStatusChip job={job} canEdit onChanged={onChanged} />
          </Field>
          <Field label="Bid due">
            <span className="text-slate-700" data-testid="bid-job-due">
              <b>{d10(job.bid_due_internal)}</b> internal · GC {d10(job.bid_due_at)}
            </span>
          </Field>
          <Field label="Start">
            <Input type="date" value={dates.start_date} onBlur={() => patch(dates)}
                   onChange={(e) => setDates({ ...dates, start_date: e.target.value })}
                   className="h-7 w-40 text-xs" data-testid="bid-job-start" />
          </Field>
          <Field label="Completion">
            <Input type="date" value={dates.completion_date} onBlur={() => patch(dates)}
                   onChange={(e) => setDates({ ...dates, completion_date: e.target.value })}
                   className="h-7 w-40 text-xs" data-testid="bid-job-completion" />
          </Field>

          {[["estimator", "Estimator", job.estimator_email], ["am", "AM", job.account_manager_email]].map(([key, label, email]) => (
            <Field key={key} label={label}>
              <select value={email || ""} disabled={busy} data-testid={`bid-job-${key}-select`}
                      onChange={(e) => {
                        const list = key === "estimator" ? people.estimators : people.account_managers;
                        const person = list.find((p) => p.email === e.target.value);
                        run(() => preconApi.assign(job.id, key === "estimator"
                          ? { estimator_email: e.target.value, estimator_name: person?.name || "" }
                          : { account_manager_email: e.target.value, account_manager_name: person?.name || "" }),
                          `${label} set`);
                      }}
                      className="h-7 w-56 rounded-md border border-slate-200 px-2 text-xs">
                <option value="">unassigned</option>
                {(key === "estimator" ? people.estimators : people.account_managers).map((p) => (
                  <option key={p.email} value={p.email}>{p.name || p.email}</option>))}
                {email && !(key === "estimator" ? people.estimators : people.account_managers).some((p) => p.email === email) && (
                  <option value={email}>{email}</option>)}
              </select>
            </Field>
          ))}

          {(proposal.estimator?.email || proposal.account_manager?.email) && (
            <div className="mt-2 flex flex-wrap items-center gap-2 rounded bg-slate-50 px-2 py-1.5 text-[11px] text-slate-600" data-testid="bid-job-assignment-proposal">
              <UserCheck className="h-3.5 w-3.5 text-slate-400" />
              Proposed: {proposal.estimator?.name || proposal.estimator?.email || "no estimator rule matched"}
              {proposal.estimator?.reason ? ` (${proposal.estimator.reason})` : ""}
              {proposal.account_manager?.email ? ` · AM ${proposal.account_manager?.name || proposal.account_manager.email} (${proposal.account_manager.reason})` : ""}
              <Button size="sm" className="h-6 text-[11px]" disabled={busy} data-testid="bid-job-accept-assignment"
                      onClick={() => run(() => preconApi.assign(job.id, {
                        estimator_email: proposal.estimator?.email || "", estimator_name: proposal.estimator?.name || "",
                        account_manager_email: proposal.account_manager?.email || "", account_manager_name: proposal.account_manager?.name || "",
                      }), "Assigned")}>Confirm</Button>
            </div>
          )}
        </Section>

        <Section title={`Customer(s) (${(job.invitations || []).length})`}>
          <div className="space-y-1.5" data-testid="bid-job-customers">
            {(job.invitations || []).map((inv) => (
              <div key={inv.id} className="rounded border border-slate-200 px-2.5 py-2 text-xs" data-testid={`customer-${inv.id}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium text-slate-800">{inv.gc_name}</span>
                  {inv.hit_rate != null && (
                    <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] text-emerald-700" data-testid={`customer-hit-${inv.id}`}>{inv.hit_rate}% hit</span>)}
                </div>
                <div className="pt-0.5 text-[11px] text-slate-500">
                  {inv.contact_name || "— no contact —"}
                  {inv.contact_email ? ` · ${inv.contact_email}` : ""}
                  {inv.contact_phone ? ` · ${inv.contact_phone}` : ""}
                </div>
              </div>))}
            {!(job.invitations || []).length && <p className="text-[11px] text-slate-400">No customers on this job.</p>}
          </div>
        </Section>

        <Section title={`GC invitations (${(job.invitations || []).length})`}>
          <div className="space-y-1.5">
            {(job.invitations || []).map((inv) => (
              <div key={inv.id} className="rounded border border-slate-200 px-2.5 py-2" data-testid={`invitation-${inv.id}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs font-medium text-slate-800">{inv.gc_name}</span>
                  <span className="text-[11px] text-slate-400">due {inv.bid_due_at || "—"} · walk {inv.walkthrough_at || "—"} · {inv.portal}</span>
                  <Select value={declining[inv.id] ? "Declined" : inv.status}
                          onValueChange={(v) => {
                            if (v === "Declined" && inv.status !== "Declined") { setDeclining((d) => ({ ...d, [inv.id]: Date.now() })); return; }
                            setDeclining((d) => ({ ...d, [inv.id]: 0 }));
                            run(() => preconApi.setInvitationStatus(job.id, inv.id, v, reasons[inv.id] || ""), `${inv.gc_name}: ${v}`);
                          }}>
                    <SelectTrigger className="ml-auto h-6 w-28 text-[11px]" data-testid={`invitation-status-${inv.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent onCloseAutoFocus={(e) => { if (declining[inv.id]) e.preventDefault(); }}>
                      {(job.invitation_statuses || ["Invited", "Bidding", "Declined", "Submitted", "Won", "Lost"]).map((s) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>))}
                    </SelectContent>
                  </Select>
                  {(inv.status === "Declined" || declining[inv.id]) && (
                    <Input value={reasons[inv.id] ?? inv.declined_reason ?? ""} placeholder="Why did they decline?"
                           autoFocus={!!declining[inv.id]}
                           onChange={(e) => setReasons({ ...reasons, [inv.id]: e.target.value })}
                           onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
                           onBlur={() => {
                             const reason = (reasons[inv.id] ?? inv.declined_reason ?? "").trim();
                             if (!reason) {
                               if (declining[inv.id] && Date.now() - declining[inv.id] > 600) { setDeclining((d) => ({ ...d, [inv.id]: 0 })); toast.error("A decline needs a reason"); }
                               return;
                             }
                             if (reason === (inv.declined_reason || "") && inv.status === "Declined") return;
                             setDeclining((d) => ({ ...d, [inv.id]: 0 }));
                             run(() => preconApi.setInvitationStatus(job.id, inv.id, "Declined", reason), `${inv.gc_name}: declined`);
                           }}
                           className="h-6 w-40 text-[11px]" data-testid={`invitation-reason-${inv.id}`} />)}
                  <button className="text-slate-400 hover:text-red-600" data-testid={`invitation-remove-${inv.id}`}
                          onClick={() => run(() => preconApi.dropInvitation(job.id, inv.id), "Invitation removed")}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
                {inv.merged_note && <p className="pt-1 text-[10px] text-slate-400">{inv.merged_note}</p>}
              </div>))}
            {!(job.invitations || []).length && <p className="text-[11px] text-slate-400">No GC invitations on this job.</p>}
          </div>
        </Section>

        <Section title="Fit rate">
          <div className="grid grid-cols-2 gap-x-6 gap-y-1 md:grid-cols-3" data-testid="bid-job-fit-breakdown">
            {Object.entries(job.fit_components || {}).map(([key, value]) => (
              <div key={key} className="flex items-baseline justify-between border-b border-slate-100 py-1">
                <span className="text-[11px] text-slate-500">{FIT_LABEL[key] || key}</span>
                <span className={`text-xs ${value === null ? "text-slate-300" : "text-slate-800"}`}>
                  {value === null ? "not known" : value}
                  <span className="ml-1 text-[10px] text-slate-400">×{(job.fit_weights || {})[key] ?? 0}</span>
                </span>
              </div>))}
          </div>
        </Section>

        <Section title="Estimate">
          {job.estimate_id ? (
            <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="bid-job-estimate">
              <button onClick={() => openEstimate(job.estimate_id)} title="Open the Estimate workspace" data-testid="bid-job-estimate-link"
                      className="rounded bg-slate-900 px-1.5 py-0.5 font-mono text-[10px] text-white transition-colors hover:bg-[#007BFF]">{job.estimate_number || "estimate"}</button>
              <span className="rounded bg-blue-100 px-1.5 py-0.5 text-[10px] text-blue-700" data-testid="bid-job-estimate-stage">{estimate?.stage || "Takeoff"}</span>
              <span className="text-slate-500">{estimate?.lines ?? 0} lines · {estimate?.recipients ?? 0} proposal recipients</span>
            </div>
          ) : (
            <p className="text-[11px] text-slate-400" data-testid="bid-job-no-estimate">
              No estimate yet. Moving this job to <b>New Bid</b> opens one, pre-filled, with every live GC as a proposal recipient.
            </p>
          )}
        </Section>

        {busy && <div className="px-5 py-2 text-[11px] text-slate-400"><Loader2 className="mr-1 inline h-3 w-3 animate-spin" /> working…</div>}
      </SheetContent>
    </Sheet>
  );
};
